import React, { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { getCategoryById, STATUS } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { MatterGroups } from "@/components/MatterGroups";
import { MatterSecciones } from "@/components/MatterSecciones";
export default function AreaView() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { matters: allMatters, viviendas, familyMembers } = useData();
  const cat = getCategoryById(categoryId);
  const [showControlled, setShowControlled] = useState(false);

  if (categoryId === "familia") return <Navigate to="/personas" replace />;

  if (!cat) {
    return (
      <div className="text-center py-20">
        <p className="text-lg text-[#6E7A74]">Área no encontrada.</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 min-h-[52px] px-6 rounded-xl bg-[#3A6B56] text-white font-semibold"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  const Icon = Icons[cat.icon] || Icons.Folder;
  const matters = allMatters.filter((m) => m.category === cat.match);
  const controlledCount = matters.filter((m) => m.status === STATUS.CONTROLLED).length;

  return (
    <div className="space-y-5">
      <button
        data-testid="area-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base min-h-[44px]"
      >
        <Icons.ChevronLeft size={22} /> Volver
      </button>

      <header className="aldia-fade-up flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
          <Icon size={26} className="text-[#3A6B56]" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">
            {cat.label}
          </h1>
          <p className="text-base text-[#6E7A74]">
            {matters.length} {matters.length === 1 ? "asunto" : "asuntos"} en esta área
          </p>
        </div>
      </header>

      {categoryId === "hogar" ? (
        <div className="space-y-6" data-testid="hogar-por-vivienda">
          {viviendas.length === 0 && (
            <button
              data-testid="hogar-crear-vivienda"
              onClick={() => navigate("/viviendas")}
              className="w-full min-h-[52px] rounded-2xl bg-[#E8F0EC] text-[#2E5645] font-semibold text-base border border-[#CDE3D6] hover:bg-[#DCEAE1] flex items-center justify-center gap-2"
            >
              <Icons.Building2 size={20} /> Crea una vivienda para agrupar sus suministros
            </button>
          )}
          {viviendas.map((viv) => {
            const vm = matters.filter((m) => m.vivienda_id === viv.id);
            return (
              <section key={viv.id} data-testid={`hogar-vivienda-${viv.id}`}>
                <button
                  onClick={() => navigate(`/vivienda/${viv.id}`)}
                  className="w-full flex items-center gap-2.5 mb-3 text-left"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0">
                    <Icons.Home size={18} className="text-[#3A6B56]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-heading font-bold text-lg text-[#1C2421] truncate">{viv.alias}</h2>
                    <p className="text-sm text-[#6E7A74] truncate">{vm.length} {vm.length === 1 ? "asunto" : "asuntos"}</p>
                  </div>
                  <Icons.ChevronRight size={20} className="text-[#C4BDB0] shrink-0" />
                </button>
                <MatterGroups matters={vm} hideControlled={!showControlled} emptyText="Sin suministros ni asuntos todavía." />
              </section>
            );
          })}
          {(() => {
            const sinViv = matters.filter((m) => !m.vivienda_id);
            if (sinViv.length === 0) return null;
            return (
              <section data-testid="hogar-sin-vivienda">
                <h2 className="font-heading font-bold text-lg text-[#1C2421] mb-3">Sin vivienda asignada</h2>
                <MatterGroups matters={sinViv} hideControlled={!showControlled} emptyText="" />
              </section>
            );
          })()}
        </div>
      ) : categoryId === "contratos" ? (
        (() => {
          const personas = (familyMembers || []).slice().sort((a, b) => {
            if (a.es_titular) return -1;
            if (b.es_titular) return 1;
            return 0;
          });
          const sinPersona = matters.filter((m) => !m.family_member_id);
          const bloques = personas
            .map((p) => ({
              p,
              items: matters.filter((m) =>
                m.family_member_id === p.id || (p.es_titular && (m.family_member_id === "fm-self" || !m.family_member_id))
              ),
            }))
            .filter((b) => b.items.length > 0);
          // Evitar duplicar los "sin persona" si ya cayeron en el titular
          const titularTiene = bloques.some((b) => b.p.es_titular);
          if (matters.length === 0) {
            return (
              <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-2xl p-6 text-center" data-testid="contratos-empty">
                No hay contratos ni seguros personales por ahora.
              </p>
            );
          }
          return (
            <div className="space-y-7" data-testid="contratos-por-persona">
              {bloques.map(({ p, items }) => (
                <section key={p.id} data-testid={`contratos-persona-${p.id}`}>
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0">
                      <Icons.User size={18} className="text-[#3A6B56]" />
                    </div>
                    <h2 className="font-heading font-bold text-lg text-[#1C2421]">
                      {p.name}{p.es_titular ? " (Titular)" : p.relation ? ` · ${p.relation}` : ""}
                    </h2>
                  </div>
                  <MatterSecciones matters={items} scheme="persona" testidPrefix={`contratos-sec-${p.id}`}
                    emptyText="Sin contratos ni seguros." />
                </section>
              ))}
              {!titularTiene && sinPersona.length > 0 && (
                <section data-testid="contratos-sin-persona">
                  <h2 className="font-heading font-bold text-lg text-[#1C2421] mb-3">Sin persona asignada</h2>
                  <MatterSecciones matters={sinPersona} scheme="persona" testidPrefix="contratos-sec-none" emptyText="" />
                </section>
              )}
            </div>
          );
        })()
      ) : (
        <MatterGroups
          matters={matters}
          hideControlled={!showControlled}
          historico
          emptyText={`No hay asuntos en ${cat.label} por ahora.`}
        />
      )}

      {controlledCount > 0 && categoryId !== "contratos" && (
        <button
          data-testid="area-toggle-controlled"
          onClick={() => setShowControlled((v) => !v)}
          className="w-full min-h-[52px] rounded-2xl bg-[#F0FDF4] text-[#15803D] font-semibold text-base border border-[#16A34A33] hover:bg-[#DCFCE7] transition-colors flex items-center justify-center gap-2"
        >
          <Icons.ShieldCheck size={20} />
          {showControlled ? "Ocultar documentación al día" : `Ver documentación al día (${controlledCount})`}
        </button>
      )}
    </div>
  );
}
